import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft, UploadCloud, Download, Loader2, CheckCircle2, AlertTriangle, Boxes,
  FileSpreadsheet, UserPlus,
} from "lucide-react";
import { adminApi, formatApiError } from "@/lib/adminAuth";
import { ADMIN } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";

// Mismas columnas que acepta el backend (Fix C, 2026-09-01). El orden coincide con el pedido.
const CAMPOS_IMPORTACION = [
  { clave: "vin", etiqueta: "VIN", obligatorio: true },
  { clave: "tipo", etiqueta: "Tipo", obligatorio: true },
  { clave: "cliente", etiqueta: "Cliente", obligatorio: true },
  { clave: "marca", etiqueta: "Marca", obligatorio: true },
  { clave: "modelo", etiqueta: "Modelo", obligatorio: true },
  // Fix H (2026-09-14): el año y el color dejaron de ser obligatorios.
  { clave: "anio", etiqueta: "Año", obligatorio: false },
  { clave: "color", etiqueta: "Color", obligatorio: false },
  { clave: "origen", etiqueta: "Origen", obligatorio: false },
  { clave: "destino", etiqueta: "Destino", obligatorio: false },
  { clave: "puerto_origen", etiqueta: "Puerto de origen", obligatorio: false },
  { clave: "puerto_destino", etiqueta: "Puerto de destino", obligatorio: false },
  { clave: "buque", etiqueta: "Buque", obligatorio: false },
  { clave: "booking", etiqueta: "Booking", obligatorio: false },
  { clave: "pedido_cliente", etiqueta: "Pedido del cliente", obligatorio: false },
  { clave: "numero_factura", etiqueta: "Número de factura", obligatorio: false },
  { clave: "fecha_estimada_entrega", etiqueta: "Entrega estimada", obligatorio: false },
];

const normalizarEtiqueta = (s) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");

const descargarEjemplo = () => {
  const encabezados = CAMPOS_IMPORTACION.map((c) => c.etiqueta);
  const filaEjemplo = [
    "1HGCM82633A004352", "camion", "Navitrans Colombia", "Kenworth", "T680", "2023", "Blanco",
    "Laredo, México", "Bogotá, Colombia", "Altamira", "Cartagena", "MSC Anahita",
    "MX-88213", "PO-1001", "FAC-2026-001", "2026-12-01",
  ];
  const filas = [encabezados, filaEjemplo]
    .map((f) => f.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob(["\ufeff" + filas], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "planilla_ejemplo_unidades.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default function ImportarUnidades() {
  usePageTitle("Importar unidades");
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [paso, setPaso] = useState("subir"); // subir | mapear | resultado
  const [archivo, setArchivo] = useState(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [cargandoColumnas, setCargandoColumnas] = useState(false);
  const [columnas, setColumnas] = useState([]);
  const [mapeo, setMapeo] = useState({});
  const [analizando, setAnalizando] = useState(false);
  const [analisis, setAnalisis] = useState(null);
  const [error, setError] = useState("");
  const [confirmando, setConfirmando] = useState(false);
  const [resultado, setResultado] = useState(null);
  // Fix G (2026-09-10): estado del formato reconocido. Cuando el backend identifica una
  // planilla Yard Booking, el mapeo ya viene resuelto y lo único que hace falta es
  // completar lo que ESA planilla no trae: Categoría (tipo), Color y Año.
  const [formato, setFormato] = useState("generico");
  const [booking, setBooking] = useState(null);
  const [faltantes, setFaltantes] = useState({});
  const [completar, setCompletar] = useState({ tipo: "", color: "", anio: "" });
  const { data: tipos = [] } = useQuery({
    queryKey: ["tipos"],
    queryFn: () => adminApi.get("/tipos").then((r) => r.data),
  });
  const esYard = formato === "yard_booking";
  const camposAcompletar = Object.keys(faltantes).filter((c) => faltantes[c] > 0);
  // Fix H (2026-09-14): solo el tipo frena el análisis. El año y el color pueden quedar
  // vacíos: el cliente los manda después y se completan sin volver a subir la planilla.
  const faltaCompletar = camposAcompletar.includes("tipo") && !(completar.tipo || "").trim();

  const subirArchivo = async (file) => {
    setError("");
    setArchivo(file);
    setAnalisis(null);
    setResultado(null);
    setCompletar({ tipo: "", color: "", anio: "" });
    setCargandoColumnas(true);
    try {
      const fd = new FormData();
      fd.append("archivo", file);
      fd.append("mapeo", "{}");
      const { data } = await adminApi.post("/admin/unidades/importar/analizar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setColumnas(data.columnas_archivo);
      // Fix G (2026-09-10): si el backend reconoció la planilla del cliente, el mapeo ya
      // está hecho y el paso de elegir columnas no tiene sentido. Esta primera llamada
      // sirve solo para saber QUÉ falta; el análisis de verdad se hace después, con los
      // valores que complete el operador.
      setFormato(data.formato || "generico");
      setBooking(data.booking || null);
      setFaltantes(data.faltantes || {});
      if (data.formato !== "yard_booking") {
        const auto = {};
        CAMPOS_IMPORTACION.forEach((c) => {
          const encontrada = data.columnas_archivo.find(
            (col) => normalizarEtiqueta(col) === normalizarEtiqueta(c.etiqueta),
          );
          if (encontrada) auto[c.clave] = encontrada;
        });
        setMapeo(auto);
      }
      setPaso("mapear");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setCargandoColumnas(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setArrastrando(false);
    const file = e.dataTransfer.files?.[0];
    if (file) subirArchivo(file);
  };

  const analizar = async () => {
    setAnalizando(true);
    setError("");
    setAnalisis(null);
    try {
      const fd = new FormData();
      fd.append("archivo", archivo);
      fd.append("mapeo", JSON.stringify(mapeo));
      // Fix G (2026-09-10): valores con los que se completa lo que la planilla deja vacío.
      // Se mandan solo los que el operador llenó. El backend nunca pisa un dato que sí
      // vino en la planilla: estos rellenan únicamente las celdas vacías.
      fd.append("defaults", JSON.stringify(
        Object.fromEntries(Object.entries(completar).filter(([, v]) => (v || "").trim())),
      ));
      const { data } = await adminApi.post("/admin/unidades/importar/analizar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAnalisis(data);
      if (data.booking) setBooking(data.booking);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setAnalizando(false);
    }
  };

  const confirmar = async () => {
    if (!analisis?.token) return;
    setConfirmando(true);
    setError("");
    try {
      const { data } = await adminApi.post("/admin/unidades/importar/confirmar", { token: analisis.token });
      setResultado(data);
      setPaso("resultado");
      toast.success(`${data.creadas} unidad${data.creadas === 1 ? "" : "es"} creada${data.creadas === 1 ? "" : "s"}`);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setConfirmando(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-8 py-8">
      <Link to="/admin/unidades" className="mb-5 inline-flex items-center gap-1.5 text-sm font-medium text-[var(--tc-text-secondary)] hover:text-[var(--tc-accent)]">
        <ArrowLeft size={15} /> Volver a unidades
      </Link>
      <h1 className="text-xl font-semibold text-[var(--tc-text)]">Importar unidades desde planilla</h1>
      <p className="mt-1 text-sm text-[var(--tc-text-secondary)]">
        Subí un archivo .xlsx o .csv y revisá qué va a pasar antes de crear nada. Si es una
        planilla Yard Booking, las columnas se reconocen solas.
      </p>

      {error && (
        <p data-testid={ADMIN.importError} className="mt-4 rounded-[var(--tc-radius)] bg-red-50 px-3.5 py-2.5 text-sm text-red-600">{error}</p>
      )}

      {paso === "subir" && (
        <div className="mt-6 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6">
          <div
            data-testid={ADMIN.importDropzone}
            onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
            onDragLeave={() => setArrastrando(false)}
            onDrop={onDrop}
            onClick={() => !cargandoColumnas && inputRef.current?.click()}
            className={`flex flex-col items-center justify-center gap-2 rounded-[var(--tc-radius)] border-2 border-dashed p-10 text-center transition-colors ${
              arrastrando ? "cursor-pointer border-[var(--tc-accent)] bg-[var(--tc-accent-soft)]" : "cursor-pointer border-[var(--tc-border)] hover:border-[var(--tc-accent)]"
            }`}
          >
            <input
              data-testid={ADMIN.importFileInput}
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              hidden
              onChange={(e) => e.target.files?.[0] && subirArchivo(e.target.files[0])}
            />
            {cargandoColumnas ? (
              <>
                <Loader2 className="animate-spin text-[var(--tc-accent)]" size={24} />
                <p className="text-sm text-[var(--tc-text-secondary)]">Leyendo el archivo...</p>
              </>
            ) : (
              <>
                <UploadCloud size={24} className="text-[var(--tc-accent)]" />
                <p className="text-sm text-[var(--tc-text-secondary)]">Arrastrá tu planilla acá o hacé clic para seleccionarla</p>
                <p className="text-xs text-[var(--tc-text-secondary)]">.xlsx o .csv, hasta 500 filas</p>
              </>
            )}
          </div>

          <div className="mt-5 flex flex-wrap items-start justify-between gap-4 border-t border-[var(--tc-border)] pt-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--tc-text-secondary)]">Columnas admitidas</p>
              <p className="mt-1.5 text-xs leading-relaxed text-[var(--tc-text-secondary)]">
                {CAMPOS_IMPORTACION.map((c) => c.etiqueta + (c.obligatorio ? " *" : "")).join(", ")}
              </p>
            </div>
            <button
              data-testid={ADMIN.importSampleLink}
              onClick={descargarEjemplo}
              className="inline-flex shrink-0 items-center gap-2 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-4 py-2.5 text-sm font-medium text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)]"
            >
              <Download size={15} /> Descargar planilla de ejemplo
            </button>
          </div>
        </div>
      )}

      {paso === "mapear" && (
        <div className="mt-6 flex flex-col gap-5">
          {/* Fix G (2026-09-10): dos caminos. Si la planilla es una Yard Booking del
              cliente, el mapeo ya está resuelto y solo se completa lo que falta. Si es un
              archivo genérico, sigue el mapeo manual de columnas de siempre. */}
          {esYard ? (
            <div className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--tc-state-done-soft)] px-3 py-1.5 text-sm font-semibold text-[var(--tc-state-done)]">
                  <FileSpreadsheet size={14} /> Planilla reconocida
                </span>
                {booking && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--tc-accent-soft)] px-3 py-1.5 text-sm font-semibold text-[var(--tc-accent)]">
                    <Boxes size={14} /> Booking {booking}
                  </span>
                )}
                <span className="text-xs text-[var(--tc-text-secondary)]">{archivo?.name}</span>
              </div>

              <p className="mt-3 text-sm text-[var(--tc-text-secondary)]">
                {camposAcompletar.length > 0
                  ? "Las columnas se mapearon solas. Esta planilla no trae los datos de abajo. El tipo hace falta para crear las unidades; el año y el color podés dejarlos vacíos y completarlos más adelante, cuando el cliente los mande."
                  : "Las columnas se mapearon solas y no falta ningún dato. Podés analizar la planilla directamente."}
              </p>

              {camposAcompletar.length > 0 && (
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  {faltantes.tipo > 0 && (
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">
                        Tipo{" "}
                        <span className="font-normal text-[var(--tc-text-secondary)]">({faltantes.tipo} sin dato)</span>
                      </label>
                      <select
                        data-testid={ADMIN.importDefaultTipo}
                        value={completar.tipo}
                        onChange={(e) => setCompletar((c) => ({ ...c, tipo: e.target.value }))}
                        className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]"
                      >
                        <option value="">Elegí un tipo</option>
                        {tipos.map((t) => (
                          <option key={t.id} value={t.id}>{t.nombre}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {faltantes.color > 0 && (
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">
                        Color{" "}
                        <span className="font-normal text-[var(--tc-text-secondary)]">(opcional · {faltantes.color} sin dato)</span>
                      </label>
                      <input
                        data-testid={ADMIN.importDefaultColor}
                        value={completar.color}
                        onChange={(e) => setCompletar((c) => ({ ...c, color: e.target.value }))}
                        placeholder="Blanco"
                        className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]"
                      />
                    </div>
                  )}
                  {faltantes.anio > 0 && (
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">
                        Año{" "}
                        <span className="font-normal text-[var(--tc-text-secondary)]">(opcional · {faltantes.anio} sin dato)</span>
                      </label>
                      <input
                        data-testid={ADMIN.importDefaultAnio}
                        value={completar.anio}
                        onChange={(e) => setCompletar((c) => ({ ...c, anio: e.target.value.replace(/[^0-9]/g, "").slice(0, 4) }))}
                        inputMode="numeric"
                        placeholder="2026"
                        className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]"
                      />
                    </div>
                  )}
                </div>
              )}

              <p className="mt-3 text-xs text-[var(--tc-text-secondary)]">
                Estos valores solo rellenan lo que la planilla deja vacío. Lo que la planilla sí trae nunca se pisa.
              </p>

              <div className="mt-5 flex items-center gap-3">
                <button
                  data-testid={ADMIN.importAnalyzeButton}
                  onClick={analizar}
                  disabled={faltaCompletar || analizando}
                  className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)] disabled:opacity-60"
                >
                  {analizando && <Loader2 size={16} className="animate-spin" />} Analizar planilla
                </button>
                <button
                  onClick={() => { setPaso("subir"); setArchivo(null); setAnalisis(null); setFormato("generico"); setFaltantes({}); setBooking(null); }}
                  className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-4 py-2.5 text-sm font-medium text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)]"
                >
                  Elegir otro archivo
                </button>
              </div>
            </div>
          ) : (
          <div className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--tc-text-secondary)]">
              Mapeo de columnas — {archivo?.name}
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {CAMPOS_IMPORTACION.map((c) => (
                <div key={c.clave}>
                  <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">
                    {c.etiqueta}{c.obligatorio && <span className="text-red-500"> *</span>}
                  </label>
                  <select
                    data-testid={ADMIN.importColumnSelect}
                    value={mapeo[c.clave] || ""}
                    onChange={(e) => setMapeo((m) => ({ ...m, [c.clave]: e.target.value || undefined }))}
                    className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]"
                  >
                    <option value="">No mapear</option>
                    {columnas.map((col) => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <div className="mt-5 flex items-center gap-3">
              <button
                data-testid={ADMIN.importAnalyzeButton}
                onClick={analizar}
                disabled={!mapeo.vin || analizando}
                className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)] disabled:opacity-60"
              >
                {analizando && <Loader2 size={16} className="animate-spin" />} Analizar planilla
              </button>
              <button
                onClick={() => { setPaso("subir"); setArchivo(null); setAnalisis(null); }}
                className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-4 py-2.5 text-sm font-medium text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)]"
              >
                Elegir otro archivo
              </button>
            </div>
          </div>
          )}

          {analisis && (
            <div className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6">
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--tc-accent-soft)] px-3 py-1.5 text-sm font-semibold text-[var(--tc-accent)]">
                  <CheckCircle2 size={14} /> {analisis.filas_validas} lista{analisis.filas_validas === 1 ? "" : "s"} para crear
                </span>
                {analisis.problemas.length > 0 && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-600">
                    <AlertTriangle size={14} /> {analisis.problemas.length} con problema{analisis.problemas.length === 1 ? "" : "s"}
                  </span>
                )}
                <span className="text-xs text-[var(--tc-text-secondary)]">de {analisis.total_filas} filas leídas</span>
              </div>

              {analisis.problemas.length > 0 && (
                <div className="mt-4 overflow-x-auto rounded-[var(--tc-radius)] border border-[var(--tc-border)]">
                  <table data-testid={ADMIN.importProblemsTable} className="w-full min-w-[520px] text-sm">
                    <thead>
                      <tr className="border-b border-[var(--tc-border)] text-left text-xs font-medium uppercase tracking-wide text-[var(--tc-text-secondary)]">
                        <th className="px-4 py-2.5">Fila</th>
                        <th className="px-4 py-2.5">Dato</th>
                        <th className="px-4 py-2.5">Motivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analisis.problemas.map((p, i) => (
                        <tr key={i} data-testid={ADMIN.importProblemRow} className="border-b border-[var(--tc-border)] last:border-0">
                          <td className="px-4 py-2.5 font-mono text-xs text-[var(--tc-text-secondary)]">{p.fila}</td>
                          <td className="px-4 py-2.5 font-mono text-xs text-[var(--tc-text)]">{p.dato || "—"}</td>
                          <td className="px-4 py-2.5 text-[var(--tc-text-secondary)]">{p.motivo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Fix G (2026-09-10): los clientes que la planilla nombra y todavía no
                  existen se crean al confirmar. Se avisa ANTES, para que el operador
                  pueda frenar si el nombre viene mal escrito. */}
              {analisis.clientes_nuevos?.length > 0 && (
                <p
                  data-testid={ADMIN.importClientesNuevos}
                  className="mt-4 flex items-start gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent-soft)] px-3.5 py-2.5 text-sm text-[var(--tc-accent)]"
                >
                  <UserPlus size={15} className="mt-0.5 shrink-0" />
                  <span>
                    Se van a crear {analisis.clientes_nuevos.length === 1 ? "estos clientes nuevos" : "estos clientes nuevos"}:{" "}
                    <strong>{analisis.clientes_nuevos.join(", ")}</strong>. Si alguno está mal escrito, corregí la planilla antes de confirmar.
                  </span>
                </p>
              )}

              <button
                data-testid={ADMIN.importConfirmButton}
                onClick={confirmar}
                disabled={!analisis.filas_validas || confirmando}
                className="mt-5 inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)] disabled:opacity-60"
              >
                {confirmando && <Loader2 size={16} className="animate-spin" />}
                Crear {analisis.filas_validas || 0} unidad{analisis.filas_validas === 1 ? "" : "es"}
              </button>
            </div>
          )}
        </div>
      )}

      {paso === "resultado" && resultado && (
        <div data-testid={ADMIN.importResultSummary} className="mt-6 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--tc-accent-soft)] px-3 py-1.5 text-sm font-semibold text-[var(--tc-accent)]">
            <CheckCircle2 size={14} /> {resultado.creadas} unidad{resultado.creadas === 1 ? "" : "es"} creada{resultado.creadas === 1 ? "" : "s"}
          </span>

          {resultado.vins.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {resultado.vins.map((vin) => (
                <span key={vin} data-testid={ADMIN.importResultVin} className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-2.5 py-1 font-mono text-xs text-[var(--tc-text)]">{vin}</span>
              ))}
            </div>
          )}

          {resultado.clientes_creados?.length > 0 && (
            <p className="mt-4 flex items-start gap-2 text-sm text-[var(--tc-text-secondary)]">
              <UserPlus size={15} className="mt-0.5 shrink-0" />
              <span>Clientes creados en esta importación: <strong className="text-[var(--tc-text)]">{resultado.clientes_creados.join(", ")}</strong></span>
            </p>
          )}

          {resultado.booking && (
            <Link
              data-testid={ADMIN.importEmbarqueLink}
              to={`/admin/embarques/${encodeURIComponent(resultado.booking)}`}
              className="mt-5 inline-flex items-center gap-2 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-4 py-2.5 text-sm font-medium text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)]"
            >
              <Boxes size={15} /> Ver embarque {resultado.booking}
            </Link>
          )}

          <div className="mt-5 flex gap-3">
            <button
              data-testid={ADMIN.importBackButton}
              onClick={() => navigate("/admin/unidades")}
              className="rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[var(--tc-accent-hover)]"
            >
              Volver a unidades
            </button>
            <button
              onClick={() => navigate("/admin/unidades/fotos")}
              className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-4 py-2.5 text-sm font-medium text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)]"
            >
              Cargar fotos masivas
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
